import dayjs from 'dayjs'
import { get, isEqual } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { Control, UseFormSetValue, useWatch } from 'react-hook-form'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { calculateConfig, DEFAULT_LOCAL_IN_OUT, TYPECARORDER } from '../common/contants'
import Toast from '../components/toast'
import { FilterAdvancedType } from '../types'
import OrderType, {
  expenseOrder,
  FeeType,
  FormValuesOrder,
  locationOrder,
  orderChange,
  revenueOrder
} from '../types/order'
import ShipmentType, { FormValuesOrderShipment } from '../types/shipment'
import { DetailTotalCostGroupOrder } from '../types/shipment/costGroup'
import { authStore } from './selectedStore'
import type { AppDispatch, RootState } from './store'

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
export const Numberformat = function (number: number) {
  return new Intl.NumberFormat('vi-VN', {
    // minimumFractionDigits: 2,
  }).format(number)
}

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(handler)
  }, [value, delay])

  return debouncedValue
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type UseWatchParams = {
  name?: string | string[]
  control: Control<any>
  defaultValue?: any
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export const useDebouncedWatch = (
  params: UseWatchParams,
  // params: any,
  delay = 500
) => {
  const watchedValue = useWatch(params)
  const [debouncedValue, setDebouncedValue] = useState(watchedValue)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(watchedValue)
    }, delay)

    return () => clearTimeout(handler)
  }, [watchedValue, delay])

  return debouncedValue || ''
}

//Format curency
export const currency = function (number: number) {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND'
    // minimumFractionDigits: 2,
  }).format(number)
}

export function formatNumber(value: number): string {
  return value ? value.toLocaleString('vi-VN') : ''
}

export function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}${mm}${dd}`
}

export const handleMutation = (props: {
  loading: boolean
  isError: boolean
  isSuccess: boolean
  successMessage: string
  errorMessage: string
  refetch?: () => void
}) => {
  if (!props.loading) {
    props.isError && Toast({ text: props.errorMessage, variant: 'error' })
    props.isSuccess && Toast({ text: props.successMessage, variant: 'success' }) && props?.refetch && props?.refetch()
  }
}

// Hook nhận vào các nhóm quyền (có thể truyền nhiều mảng quyền)
export const useHasPermission = (...permissionGroups: string[][]): boolean => {
  // Lấy role từ store
  const roles = useAppSelector(authStore)?.user?.staffTypes || []

  // Nối tất cả các mảng quyền thành 1 mảng duy nhất
  const allowedPermissions = permissionGroups.flat()

  // Kiểm tra xem có ít nhất 1 quyền trong roles trùng với các quyền cho phép hay không
  return roles.some((role) => allowedPermissions.includes(role.code))
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export const convertDataLabel = ({ data, key, value }: { data: any; key: string; value: string }) => {
  const newData = data.map((e: any) => ({ ...e, label: e?.[key] || '', value: e?.[value] || '' }))
  return newData || []
}

export const convertDataLabelAutoComplate = ({
  data,
  key,
  value,
  subKey
}: {
  data: any
  key: string
  value: string
  subKey?: string
}) => {
  const newData = data.map((e: any) => ({
    ...e,
    label: (e?.[key] || '') + (subKey ? ` - ${e?.[subKey]}` : ''),
    value: e?.[value].toString() || ''
  }))
  return newData || []
}

// Lấy param
export const useQueryParam = (paramName: string, defaultValue: any) => {
  const [searchParams] = useSearchParams()
  const paramValue = searchParams.get(paramName)

  const isJson = (str: string) => {
    try {
      JSON.parse(str)
      return true
    } catch {
      return false
    }
  }

  if (paramValue) {
    if (isJson(paramValue)) {
      try {
        const val = JSON.parse(paramValue)
        if (typeof val == 'boolean') return val
        return val || defaultValue
      } catch (error) {
        console.error(`Error parsing ${paramName}:`, error)
        return defaultValue
      }
    } else {
      // Trả về chuỗi gốc nếu không phải là JSON
      return paramValue
    }
  }

  return defaultValue
}

// Hàm tạo params từ paginationModel và filters
export const useCreateSearchParams = (
  paginationModel: { page: number; pageSize: number },
  filters: { [field: string]: string | FilterAdvancedType[] }
) => {
  const params: { [key: string]: string } = {}
  const fields = {
    page: paginationModel.page.toString(),
    pageSize: paginationModel.pageSize.toString(),
    ...filters
  }

  // Chỉ thêm các trường hợp có giá trị
  Object.entries(fields).forEach(([key, value]) => {
    if (typeof value == 'boolean') return (params[key] = value)
    if (value) {
      // Xử lý mảng nếu có
      if (Array.isArray(value)) {
        params[key] = JSON.stringify(value)
      } else {
        params[key] = value // Gán giá trị cho các trường khác
      }
    }
  })

  return params
}

//Check object rỗng
export function isEmpty(obj: any) {
  for (const prop in obj) {
    if (Object.hasOwn(obj, prop)) {
      return false
    }
  }

  return true
}

/* eslint-enable @typescript-eslint/no-explicit-any */

//Hàm tính toán thay đổi field đơn
/* eslint-disable @typescript-eslint/no-explicit-any */
export function formatNumberToFix(value: number, toFix = 2): string {
  if (Number.isNaN(value)) return '' // Xử lý nếu không phải số
  const formatted = value?.toFixed(toFix)
  return formatted.endsWith(`.${'0'.repeat(toFix)}`) ? `${Math.floor(value)}` : formatted
}

const calculateRevenueExpenditureProfit = (
  getValues: () => any,
  setValue: UseFormSetValue<any>,
  // isCompany: boolean,
  isCoordination?: boolean,
  isOliBarem?: boolean,
  isSalaryOrder?: boolean
) => {
  const allValues = getValues()

  // ==== Tổng thu ====
  const freightCollect = [
    'freightCollect.tongMonneyVanChuyen',
    'freightCollect.tongMonneyThongQuan',
    'freightCollect.comReality.monney',
    'freightCollect.phiRu.monney'
  ]
  const totalFreightCollect = freightCollect.reduce((sum, path) => {
    const val = parseFloat(get(allValues, path) || '0')
    return sum + val
  }, 0)

  // ==== Tổng chi ====
  let totalFreightPrepaid = 0
  let totalFreightCollectCompany = 0
  const trip = get(allValues, 'expenseTrip') || {}
  const totalPriceDiesel = isOliBarem ? parseFloat(trip.litDiesel || '0') * parseFloat(trip.priceDiesel || '0') : 0

  if (isCoordination) {
    const freightPrepaidCompany = ['freightPrepaid.tongMonneyVanChuyen']
    totalFreightCollectCompany = freightPrepaidCompany.reduce((sum, path) => {
      const val = parseFloat(get(allValues, path) || '0')
      return sum + val
    }, 0)
    totalFreightPrepaid =
      totalPriceDiesel +
      parseFloat(trip.etc || '0') +
      parseFloat(isSalaryOrder ? trip.salaryTrip || '0' : '0') +
      parseFloat(trip.roadFee || '0') +
      parseFloat(trip.businessTrip || '0') +
      parseFloat(trip.nightShift || '0') +
      parseFloat(trip.repair || '0') +
      parseFloat(trip.etcOther || '0') +
      parseFloat(trip.repairOther || '0') +
      parseFloat(trip.chiPhiKhac || '0')
  } else {
    const freightPrepaid = [
      'freightPrepaid.tongMonneyVanChuyen',
      'freightPrepaid.tongMonneyThongQuan',
      'freightPrepaid.phiRu.monney',
      'freightPrepaid.com.monney'
    ]
    totalFreightPrepaid = freightPrepaid.reduce((sum, path) => {
      const val = parseFloat(get(allValues, path) || '0')
      return sum + val
    }, 0)
  }

  const totalCom = parseFloat(get(allValues, 'freightPrepaid.com.monney') || '0')
  const profit = !isCoordination
    ? totalFreightCollect - totalFreightPrepaid
    : totalFreightCollectCompany - totalFreightPrepaid

  const format = (val: number) => val.toFixed(0)
  const safeSet = (path: string, value: any) => {
    const current = get(allValues, path)
    if (!isEqual(current, value)) {
      setValue(path, value, { shouldDirty: false, shouldTouch: false })
    }
  }

  safeSet('expenseTrip.totalPriceDiesel', format(totalPriceDiesel))
  safeSet(
    'feesInfo.totalFeeCollected',
    isCoordination ? format(totalFreightCollectCompany) : format(totalFreightCollect)
  )
  safeSet('feesInfo.totalCost', format(totalFreightPrepaid))
  safeSet('feesInfo.com', format(totalCom))
  safeSet('feesInfo.profit', format(profit))
}

const calculateTotalGroupWhenFieldChanges = (
  changedField: string,
  getValues: () => any,
  setValue: UseFormSetValue<any>
) => {
  // Tìm config chứa field này
  const config = calculateConfig.find((cfg) => cfg.fields.includes(changedField))
  if (!config) return

  const allValues = getValues()

  let totalMonney = 0
  let totalVat = 0
  let totalMonneyVat = 0

  for (const field of config.fields) {
    const data = get(allValues, field)

    const monney = parseFloat(data?.monney || '0')
    const vat = parseFloat(data?.monneyVat || '0')
    const total = parseFloat(data?.totalMonneyVat || '0')

    totalMonney += monney
    totalVat += vat
    totalMonneyVat += total
  }

  const safeSet = (path: string, val: any) => {
    const current = get(allValues, path)
    if (!isEqual(current, val)) {
      setValue(path, formatNumberToFix(val), { shouldDirty: false, shouldTouch: false })
    }
  }

  safeSet(config.totalFields.monney, totalMonney)
  safeSet(config.totalFields.vat, totalVat)
  safeSet(config.totalFields.monneyVat, totalMonneyVat)
}

export function useFieldCalculationHandler({
  setValue,
  getValues,
  isResetting,
  isCompany,
  isCoordination,
  isOliBarem,
  isSalaryOrder
}: {
  setValue: UseFormSetValue<any>
  getValues: () => any
  isResetting?: React.MutableRefObject<boolean>
  isCompany: boolean
  isCoordination?: boolean
  isOliBarem?: boolean
  isSalaryOrder?: boolean
}) {
  const calculateFees = useCallback((data?: FeeType, field?: string) => {
    if (!data?.monney) return { monneyVat: '', totalMonneyVat: '' }
    const checkCom = field === 'freightPrepaid.com'
    const monney = parseFloat(data.monney) || 0
    const vat = parseFloat(data?.vat || '0') || 0
    const monneyVat = (monney * vat) / 100
    const totalMonneyVat = monney + (checkCom ? -monneyVat : monneyVat)
    return {
      monneyVat: formatNumberToFix(monneyVat),
      totalMonneyVat: formatNumberToFix(totalMonneyVat)
    }
  }, [])

  const calculateTotalMoneyVatFees = useCallback((data?: FeeType, field?: string) => {
    const checkCom = field === 'freightPrepaid.com'
    const monney = parseFloat(data?.monney || '0') // Chuyển giá trị về số hoặc mặc định là 0
    const monneyVat = parseFloat(data?.monneyVat || '0') // Chuyển giá trị về số hoặc mặc định là 0
    const totalMonneyVat = monney + (checkCom ? -monneyVat : monneyVat)

    return {
      totalMonneyVat: formatNumberToFix(totalMonneyVat) // Format kết quả
    }
  }, [])

  const onFieldChange = useCallback(
    (
      field: string,
      name:
        | 'monney'
        | 'vat'
        | 'monneyVat'
        | 'litDiesel'
        | 'priceDiesel'
        | 'etc'
        | 'salaryTrip'
        | 'roadFee'
        | 'businessTrip'
        | 'nightShift'
        | 'repair'
        | 'etcOther'
        | 'repairOther'
        | 'chiPhiKhac',
      value: any
    ) => {
      setValue(`${field}.${name}`, value)
      const allValues = getValues()
      // if (isResetting?.current) return

      if (field === 'expenseTrip') {
        const litDiesel = name === 'litDiesel' ? value : get(allValues, `${field}.litDiesel`)
        const priceDiesel = name === 'priceDiesel' ? value : get(allValues, `${field}.priceDiesel`)
        // const priceEtc = name === 'etc' ? value : get(allValues, `${field}.etc`)
        // const priceSalaryTrip = name === 'salaryTrip' ? value : get(allValues, `${field}.salaryTrip`)
        // const priceRoadFee = name === 'roadFee' ? value : get(allValues, `${field}.roadFee`)
        // const priceBusinessTrip = name === 'businessTrip' ? value : get(allValues, `${field}.businessTrip`)
        // const priceChiPhiKhac = name === 'chiPhiKhac' ? value : get(allValues, `${field}.chiPhiKhac`)

        const totalPriceDiesel = parseFloat(litDiesel || '0') * parseFloat(priceDiesel || '0')

        setValue(`${field}.totalPriceDiesel`, formatNumberToFix(totalPriceDiesel), { shouldDirty: false })
      } else {
        const monney = name === 'monney' ? value : get(allValues, `${field}.monney`)
        const vat = name === 'vat' ? value : get(allValues, `${field}.vat`)
        const monneyVat = name === 'monneyVat' ? value : get(allValues, `${field}.monneyVat`)

        if (name != 'monneyVat') {
          const result = calculateFees({ monney, vat }, field)
          setValue(`${field}.monneyVat`, result.monneyVat, { shouldDirty: false })
          setValue(`${field}.totalMonneyVat`, result.totalMonneyVat, { shouldDirty: false })
        }

        if (name === 'monneyVat') {
          const result = calculateTotalMoneyVatFees({ monney, monneyVat }, field)
          setValue(`${field}.totalMonneyVat`, result.totalMonneyVat, { shouldDirty: false })
        }

        calculateTotalGroupWhenFieldChanges(field, getValues, setValue)
      }
      calculateRevenueExpenditureProfit(getValues, setValue, isCoordination, isOliBarem, isSalaryOrder)
    },
    [
      setValue,
      getValues,
      calculateFees,
      calculateTotalMoneyVatFees,
      isResetting,
      isCompany,
      isCoordination,
      isOliBarem,
      isSalaryOrder
    ]
  )

  return { onFieldChange }
}

export const calculateAndSetTotals = (
  fields: string[], // Danh sách các trường cần theo dõi
  totalFields: {
    // Các trường tổng
    monney: string
    vat: string
    monneyVat: string
  },
  control: any, // React Hook Form control
  setValue: any // React Hook Form setValue
) => {
  const multipleValues = useDebouncedWatch({ name: fields, control })

  React.useEffect(() => {
    let totalMonney = 0
    let totalVat = 0
    let totalMonneyVat = 0

    multipleValues.forEach((fee: FeeType) => {
      const monney = parseFloat(fee?.monney || '0')
      const vat = parseFloat(fee?.monneyVat || '0')
      const monneyVat = parseFloat(fee?.totalMonneyVat || '0')

      totalMonney += monney
      totalVat += vat
      totalMonneyVat += monneyVat
    })

    const safeSet = (path: string, val: any) => {
      const current = get(control._formValues, path)
      if (!isEqual(current, val)) {
        setValue(path, formatNumberToFix(val), { shouldDirty: false, shouldTouch: false })
      }
    }

    safeSet(totalFields.monney, totalMonney)
    safeSet(totalFields.vat, totalVat)
    safeSet(totalFields.monneyVat, totalMonneyVat)
  }, [multipleValues, setValue]) // Theo dõi thay đổi của các trường
}

/* eslint-enable @typescript-eslint/no-explicit-any */

//Tính Tổng chi
export const calculateTotalCost = (data: OrderType | ShipmentType): number => {
  if (!data?.expense) return 0

  const expenseKeys: (keyof expenseOrder)[] = [
    'com',
    'phiRu',
    'cuocChi',
    // 'comReality',
    'lachHuyen',
    'caXe',
    'namLai',
    'chiPhiKhac',
    'thongQuan',
    'haiQuan',
    'kiemHoa',
    'kiemDich',
    'hunTrung',
    'chiPhiKhacThongQuan'
  ]

  return expenseKeys.reduce((total, key) => total + (convertNumber(data.expense?.[key] ?? 0) || 0), 0)
}

//Tính tổng thu
export const calculateTotalFeeCollected = (data: OrderType | ShipmentType): number => {
  if (!data?.revenue) return 0

  const revenueKeys: (keyof revenueOrder)[] = [
    'cuocThu',
    'phiRu',
    'comReality',
    'lachHuyen',
    'caXe',
    'namLai',
    'chiPhiKhac',
    'thongQuan',
    'haiQuan',
    'kiemHoa',
    'kiemDich',
    'hunTrung',
    'chiPhiKhacThongQuan'
  ]

  return revenueKeys.reduce((total, key) => total + (convertNumber(data.revenue?.[key] ?? 0) || 0), 0)
}

//Tính tổng dichVuThongQuan
export const calculateTotalDVThongQuanRevenue = (data: OrderType): number => {
  if (!data?.revenue) return 0

  const revenueKeys: (keyof revenueOrder)[] = [
    'thongQuan',
    'haiQuan',
    'kiemHoa',
    'kiemDich',
    'hunTrung',
    'chiPhiKhacThongQuan'
  ]

  return revenueKeys.reduce((total, key) => total + (convertNumber(data.revenue?.[key] ?? 0) || 0), 0)
}

//Tính tổng dichVuThongQuan
export const calculateTotalDVThongQuanExpense = (data: OrderType): number => {
  if (!data?.expense) return 0

  const expenseKeys: (keyof expenseOrder)[] = [
    'thongQuan',
    'haiQuan',
    'kiemHoa',
    'kiemDich',
    'hunTrung',
    'chiPhiKhacThongQuan'
  ]

  return expenseKeys.reduce((total, key) => total + (convertNumber(data.expense?.[key] ?? 0) || 0), 0)
}

//Tính tổng chi ho thu
export const calculateTotalChiHoRevenue = (data: OrderType): number => {
  if (!data?.revenue) return 0

  const revenueKeys: (keyof revenueOrder)[] = ['csht', 'nang', 'ha', 'can', 'chiPhiKhacChiHo']

  return revenueKeys.reduce((total, key) => total + (convertNumber(data.revenue?.[key] ?? 0) || 0), 0)
}

//Tính tổng Chi ho chi
export const calculateTotalChiHoExpense = (data: OrderType): number => {
  if (!data?.expense) return 0

  const expenseKeys: (keyof expenseOrder)[] = ['csht', 'nang', 'ha', 'can', 'chiPhiKhacChiHo']

  return expenseKeys.reduce((total, key) => total + (convertNumber(data.expense?.[key] ?? 0) || 0), 0)
}

/**
 * Chuyển đổi một chuỗi ngày tháng thành chuỗi ISO nếu giá trị tồn tại.
 * @param dateValue - Giá trị ngày tháng (chuỗi hoặc undefined)
 * @returns Chuỗi ISO hoặc null nếu không hợp lệ
 */
export const convertToISODate = (dateValue?: string | null): string | null => {
  const date = dateValue ? dayjs(dateValue) : null
  return date?.isValid() ? date.toISOString() : null
}

export const convertNumber = (value?: string | number | null): number | null => {
  const n = value ? Number(value) : null
  return n
}

export const convertDateToForm = (value?: string | string | null): string | null => {
  const date = value ? dayjs(value).toString() : null
  const n = date || null
  return n
}

export const convertVat = (value?: string | null): string => {
  const n = value ? value : 'KHĐ'
  return n
}

export const convertVatToForm = (value?: string | null): string => {
  const n = value === 'KHĐ' ? '' : value ? value : ''
  return n
}

export const formatDataSubmit = (data: FormValuesOrder) => {
  const isCompany = data?.vehicleInfo?.typeTransport === TYPECARORDER.company
  return {
    reasonUpdate: data?.orderInfo?.reasonUpdate,
    pic: data?.orderInfo?.pic,
    noBill: data?.orderInfo?.noBill,
    noBooking: data?.orderInfo?.noBooking,
    noContact: data?.orderInfo?.noContact,
    noteCustomer: data?.orderInfo?.noteCustomer,
    typeTransport: data?.vehicleInfo?.typeTransport, //nhà xe: VENDOR, xe công ty: COMPANY
    noteCar: data?.vehicleInfo?.noteCar,
    noContainer: data?.goodsInfo?.noContainer,
    noSeal: data?.goodsInfo?.noSeal,
    weight: data?.goodsInfo?.weight,
    dateStart: convertToISODate(data?.transferOrder?.dateStart), //ngày bắt đầu vận chuyển
    dateEnd: convertToISODate(data?.transferOrder?.dateEnd), //ngày kết thúc vận chuyển
    placeStart: data?.transferOrder?.placeStart, //nơi lấy hàng
    placeEnd: data?.transferOrder?.placeEnd, //nơi trả hàng
    locationStartId: data?.transferOrder?.locationStartId ?? null,
    locationEndId: data?.transferOrder?.locationEndId ?? null,
    locationStart: data?.transferOrder?.locationStart,
    locationEnd: data?.transferOrder?.locationEnd,
    orderTypeId: convertNumber(data?.orderInfo?.orderTypeId), // id loại hình vận chuyển
    customerId: convertNumber(data?.orderInfo?.customerId), //id khách hàng
    vendorId: convertNumber(data?.vehicleInfo?.vendorId), // id nhà xe, bắt buộc có nếu typeTransport là VENDOR
    driverName: data?.vehicleInfo?.driverName, //tên lái xe
    licensePlate: data?.vehicleInfo?.licensePlate, //biển số xe
    driverPhoneNumber: data?.vehicleInfo?.driverPhoneNumber,
    myCarId: convertNumber(data?.vehicleInfo?.myCarId), // id xe công ty, bắt buộc có nếu typeTransport là COMPANY,
    staffDriverId: convertNumber(data?.vehicleInfo?.staffDriverId), // lái xe, bắt buộc có nếu typeTransport là COMPANY,
    staffSaleId: convertNumber(data?.orderInfo?.staffSaleId), //nhân viên sale
    staffKHId: convertNumber(data?.orderInfo?.staffKHId), // nhân viên kế hoạch
    staffTDKHId: convertNumber(data?.orderInfo?.staffTDKHId), // nhân viên theo dõi kế hoạch
    staffOPId: convertNumber(data?.orderInfo?.staffOPId), // nhân viên hiện trường
    shippingLineId: convertNumber(data?.goodsInfo?.shippingLineId), // hãng tàu
    containerId: convertNumber(data?.goodsInfo?.containerId), //id loại hình đóng gói
    /* eslint-disable @typescript-eslint/no-explicit-any */
    locationIns:
      data?.locationIns?.map((e: any) => {
        return {
          id: e.id,
          place: e.place,
          date: convertToISODate(e.date),
          nameContact: e.nameContact,
          phoneContact: e.phoneContact,
          wareHouse: e.wareHouse || '',
          gps: e.gps || '',
          note: e.note
        }
      }) || [],
    locationOuts:
      data?.locationOuts?.map((e: any) => {
        return {
          id: e.id,
          place: e.place,
          date: convertToISODate(e.date),
          nameContact: e.nameContact,
          phoneContact: e.phoneContact,
          wareHouse: e.wareHouse || '',
          gps: e.gps || '',
          note: e.note
        }
      }) || [],
    /* eslint-enable @typescript-eslint/no-explicit-any */
    revenue: {
      noteChiPhiKhacCuocThu: data?.freightCollect?.noteChiPhiKhacCuocThu,
      noteChiPhiKhacChiHo: data?.freightCollect?.noteChiPhiKhacChiHo,
      noteChiPhiKhacThongQuan: data?.freightCollect?.noteChiPhiKhacThongQuan,
      phiRu: isCompany ? null : convertNumber(data?.freightCollect?.phiRu?.monney),
      taxPhiRu: isCompany ? null : convertVat(data?.freightCollect?.phiRu?.vat),
      taxMoneyPhiRu: isCompany ? null : convertNumber(data?.freightCollect?.phiRu?.monneyVat),
      cuocThu: convertNumber(data?.freightCollect?.cuoc?.monney),
      taxCuocThu: convertVat(data?.freightCollect?.cuoc?.vat),
      taxMoneyCuocThu: convertNumber(data?.freightCollect?.cuoc?.monneyVat),
      comReality: isCompany ? null : convertNumber(data?.freightCollect?.comReality?.monney),
      taxComReality: isCompany ? null : convertVat(data?.freightCollect?.comReality?.vat),
      taxMoneyComReality: isCompany ? null : convertNumber(data?.freightCollect?.comReality?.monneyVat),
      lachHuyen: convertNumber(data?.freightCollect?.lachHuyen?.monney),
      taxLachHuyen: convertVat(data?.freightCollect?.lachHuyen?.vat),
      taxMoneyLachHuyen: convertNumber(data?.freightCollect?.lachHuyen?.monneyVat),
      caXe: convertNumber(data?.freightCollect?.caXe?.monney),
      taxCaxe: convertVat(data?.freightCollect?.caXe?.vat),
      taxMoneyCaxe: convertNumber(data?.freightCollect?.caXe?.monneyVat),
      namLai: convertNumber(data?.freightCollect?.namLai?.monney),
      taxNamLai: convertVat(data?.freightCollect?.namLai?.vat),
      taxMoneyNamLai: convertNumber(data?.freightCollect?.namLai?.monneyVat),

      cshtCuocThu: convertNumber(data?.freightCollect?.cshtCuocThu?.monney),
      taxCshtCuocThu: convertVat(data?.freightCollect?.cshtCuocThu?.vat),
      taxMoneyCshtCuocThu: convertNumber(data?.freightCollect?.cshtCuocThu?.monneyVat),

      nangCuocThu: convertNumber(data?.freightCollect?.nangCuocThu?.monney),
      taxNangCuocThu: convertVat(data?.freightCollect?.nangCuocThu?.vat),
      taxMoneyNangCuocThu: convertNumber(data?.freightCollect?.nangCuocThu?.monneyVat),

      haCuocThu: convertNumber(data?.freightCollect?.haCuocThu?.monney),
      taxHaCuocThu: convertVat(data?.freightCollect?.haCuocThu?.vat),
      taxMoneyHaCuocThu: convertNumber(data?.freightCollect?.haCuocThu?.monneyVat),

      canCuocThu: convertNumber(data?.freightCollect?.canCuocThu?.monney),
      taxCanCuocThu: convertVat(data?.freightCollect?.canCuocThu?.vat),
      taxMoneyCanCuocThu: convertNumber(data?.freightCollect?.canCuocThu?.monneyVat),
      //Chi phí khác vận chuyển
      chiPhiKhac: convertNumber(data?.freightCollect?.chiphikhacVanChuyen?.monney),
      taxChiPhiKhac: convertVat(data?.freightCollect?.chiphikhacVanChuyen?.vat),
      taxMoneyChiPhiKhac: convertNumber(data?.freightCollect?.chiphikhacVanChuyen?.monneyVat),
      //Chi phí khác Chi hộ
      chiphikhacChiHo: convertNumber(data?.freightCollect?.chiphikhacChiHo?.monney),
      taxChiPhiKhacChiHo: convertVat(data?.freightCollect?.chiphikhacChiHo?.vat),
      taxMoneyChiPhiKhacChiHo: convertNumber(data?.freightCollect?.chiphikhacChiHo?.monneyVat),
      //Chi phí khác thông quan
      chiPhiKhacThongQuan: convertNumber(data?.freightCollect?.chiphikhacThongQuan?.monney),
      taxChiPhiKhacThongQuan: convertVat(data?.freightCollect?.chiphikhacThongQuan?.vat),
      taxMoneyChiPhiKhacThongQuan: convertNumber(data?.freightCollect?.chiphikhacThongQuan?.monneyVat),
      //
      thongQuan: convertNumber(data?.freightCollect?.thongQuan?.monney),
      taxThongQuan: convertVat(data?.freightCollect?.thongQuan?.vat),
      taxMoneyThongQuan: convertNumber(data?.freightCollect?.thongQuan?.monneyVat),
      haiQuan: convertNumber(data?.freightCollect?.kyHaiQuanGiamSat?.monney),
      taxHaiQuan: convertVat(data?.freightCollect?.kyHaiQuanGiamSat?.vat),
      taxMoneyHaiQuan: convertNumber(data?.freightCollect?.kyHaiQuanGiamSat?.monneyVat),
      kiemHoa: convertNumber(data?.freightCollect?.kiemHoa?.monney),
      taxKiemHoa: convertVat(data?.freightCollect?.kiemHoa?.vat),
      taxMoneyKiemHoa: convertNumber(data?.freightCollect?.kiemHoa?.monneyVat),
      kiemDich: convertNumber(data?.freightCollect?.kiemDich?.monney),
      taxKiemDich: convertVat(data?.freightCollect?.kiemDich?.vat),
      taxMoneyKiemDich: convertNumber(data?.freightCollect?.kiemDich?.monneyVat),
      hunTrung: convertNumber(data?.freightCollect?.hunTrung?.monney),
      taxHunTrung: convertVat(data?.freightCollect?.hunTrung?.vat),
      taxMoneyHunTrung: convertNumber(data?.freightCollect?.hunTrung?.monneyVat),
      csht: convertNumber(data?.freightCollect?.coSoHaTang?.monney),
      nang: convertNumber(data?.freightCollect?.nang?.monney),
      ha: convertNumber(data?.freightCollect?.ha?.monney),
      can: convertNumber(data?.freightCollect?.can?.monney)
    },
    expense: {
      noteChiPhiKhacCuocChi: data?.freightPrepaid?.noteChiPhiKhacCuocChi,
      noteChiPhiKhacChiHo: data?.freightPrepaid?.noteChiPhiKhacChiHo,
      noteChiPhiKhacThongQuan: data?.freightPrepaid?.noteChiPhiKhacThongQuan,
      phiRu: isCompany ? null : convertNumber(data?.freightPrepaid?.phiRu?.monney),
      taxPhiRu: isCompany ? null : convertVat(data?.freightPrepaid?.phiRu?.vat),
      taxMoneyPhiRu: isCompany ? null : convertNumber(data?.freightPrepaid?.phiRu?.monneyVat),
      invoicePhiRu: data?.freightPrepaid?.phiRu?.invoiceNumber,
      dateInvoicePhiRu: convertToISODate(data?.freightPrepaid?.phiRu?.date),
      com: isCompany ? null : convertNumber(data?.freightPrepaid?.com?.monney),
      taxCom: isCompany ? null : convertVat(data?.freightPrepaid?.com?.vat),
      taxMoneyCom: isCompany ? null : convertNumber(data?.freightPrepaid?.com?.monneyVat),
      invoiceCom: data?.freightPrepaid?.com?.invoiceNumber,
      noteCom: data?.freightPrepaid?.com?.noteCom,
      cuocChi: convertNumber(data?.freightPrepaid?.cuoc?.monney),
      taxCuocChi: convertVat(data?.freightPrepaid?.cuoc?.vat),
      taxMoneyCuocChi: convertNumber(data?.freightPrepaid?.cuoc?.monneyVat),
      invoiceCuocChi: data?.freightPrepaid?.cuoc?.invoiceNumber,
      dateInvoiceCuocChi: convertToISODate(data?.freightPrepaid?.cuoc?.date),
      // comReality: convertNumber(data?.freightPrepaid?.comReality?.monney),
      // taxComReality: convertVat(data?.freightPrepaid?.comReality?.vat),
      // taxMoneyComReality: convertNumber(data?.freightPrepaid?.comReality?.monneyVat),
      // invoiceComReality: data?.freightPrepaid?.comReality?.invoiceNumber,
      lachHuyen: convertNumber(data?.freightPrepaid?.lachHuyen?.monney),
      taxLachHuyen: convertVat(data?.freightPrepaid?.lachHuyen?.vat),
      taxMoneyLachHuyen: convertNumber(data?.freightPrepaid?.lachHuyen?.monneyVat),
      caXe: convertNumber(data?.freightPrepaid?.caXe?.monney),
      taxCaXe: convertVat(data?.freightPrepaid?.caXe?.vat),
      taxMoneyCaXe: convertNumber(data?.freightPrepaid?.caXe?.monneyVat),
      namLai: convertNumber(data?.freightPrepaid?.namLai?.monney),
      taxNamLai: convertVat(data?.freightPrepaid?.namLai?.vat),
      taxMoneyNamLai: convertNumber(data?.freightPrepaid?.namLai?.monneyVat),
      chiPhiKhac: convertNumber(data?.freightPrepaid?.chiphikhacVanChuyen?.monney),
      taxChiPhiKhac: convertVat(data?.freightPrepaid?.chiphikhacVanChuyen?.vat),
      taxMoneyChiPhiKhac: convertNumber(data?.freightPrepaid?.chiphikhacVanChuyen?.monneyVat),
      thongQuan: convertNumber(data?.freightPrepaid?.thongQuan?.monney),
      taxThongQuan: convertVat(data?.freightPrepaid?.thongQuan?.vat),
      taxMoneyThongQuan: convertNumber(data?.freightPrepaid?.thongQuan?.monneyVat),
      invoiceThongQuan: data?.freightPrepaid?.thongQuan?.invoiceNumber,
      dateInvoiceThongQuan: convertToISODate(data?.freightPrepaid?.thongQuan?.date),
      haiQuan: convertNumber(data?.freightPrepaid?.kyHaiQuanGiamSat?.monney),
      taxHaiQuan: convertVat(data?.freightPrepaid?.kyHaiQuanGiamSat?.vat),
      taxMoneyHaiQuan: convertNumber(data?.freightPrepaid?.kyHaiQuanGiamSat?.monneyVat),
      invoiceHaiQuan: data?.freightPrepaid?.kyHaiQuanGiamSat?.invoiceNumber,
      dateInvoiceHaiQuan: convertToISODate(data?.freightPrepaid?.kyHaiQuanGiamSat?.date),
      kiemHoa: convertNumber(data?.freightPrepaid?.kiemHoa?.monney),
      taxKiemHoa: convertVat(data?.freightPrepaid?.kiemHoa?.vat),
      taxMoneyKiemHoa: convertNumber(data?.freightPrepaid?.kiemHoa?.monneyVat),
      invoiceKiemHoa: data?.freightPrepaid?.kiemHoa?.invoiceNumber,
      dateInvoiceKiemHoa: convertToISODate(data?.freightPrepaid?.kiemHoa?.date),
      kiemDich: convertNumber(data?.freightPrepaid?.kiemDich?.monney),
      taxKiemDich: convertVat(data?.freightPrepaid?.kiemDich?.vat),
      taxMoneyKiemDich: convertNumber(data?.freightPrepaid?.kiemDich?.monneyVat),
      invoiceKiemDich: data?.freightPrepaid?.kiemDich?.invoiceNumber,
      dateInvoiceKiemDich: convertToISODate(data?.freightPrepaid?.kiemDich?.date),
      hunTrung: convertNumber(data?.freightPrepaid?.hunTrung?.monney),
      taxHunTrung: convertVat(data?.freightPrepaid?.hunTrung?.vat),
      taxMoneyHunTrung: convertNumber(data?.freightPrepaid?.hunTrung?.monneyVat),
      invoiceHunTrung: data?.freightPrepaid?.hunTrung?.invoiceNumber,
      dateInvoiceHunTrung: convertToISODate(data?.freightPrepaid?.hunTrung?.date),
      csht: convertNumber(data?.freightPrepaid?.coSoHaTang?.monney),
      invoiceCSHT: data?.freightPrepaid?.coSoHaTang?.invoiceNumber,
      nang: convertNumber(data?.freightPrepaid?.nang?.monney),
      invoiceNang: data?.freightPrepaid?.nang?.invoiceNumber,
      ha: convertNumber(data?.freightPrepaid?.ha?.monney),
      invoiceHa: data?.freightPrepaid?.ha?.invoiceNumber,
      can: convertNumber(data?.freightPrepaid?.can?.monney),
      invoiceCan: data?.freightPrepaid?.can?.invoiceNumber,

      cshtCuocChi: convertNumber(data?.freightPrepaid?.cshtCuocChi?.monney),
      taxCshtCuocChi: convertVat(data?.freightPrepaid?.cshtCuocChi?.vat),
      taxMoneyCshtCuocChi: convertNumber(data?.freightPrepaid?.cshtCuocChi?.monneyVat),
      // invoiceCshtCuocChi: data?.freightPrepaid?.cshtCuocChi?.invoiceNumber,
      // dateCshtCuocChi: convertToISODate(data?.freightPrepaid?.cshtCuocChi?.date),

      nangCuocChi: convertNumber(data?.freightPrepaid?.nangCuocChi?.monney),
      taxNangCuocChi: convertVat(data?.freightPrepaid?.nangCuocChi?.vat),
      taxMoneyNangCuocChi: convertNumber(data?.freightPrepaid?.nangCuocChi?.monneyVat),
      // invoiceNangCuocChi: data?.freightPrepaid?.nangCuocChi?.invoiceNumber,
      // dateNangCuocChi: convertToISODate(data?.freightPrepaid?.cshtCuocChi?.date),

      haCuocChi: convertNumber(data?.freightPrepaid?.haCuocChi?.monney),
      taxHaCuocChi: convertVat(data?.freightPrepaid?.haCuocChi?.vat),
      taxMoneyHaCuocChi: convertNumber(data?.freightPrepaid?.haCuocChi?.monneyVat),
      // invoiceHaCuocChi: data?.freightPrepaid?.haCuocChi?.invoiceNumber,
      // dateHaCuocChi: convertToISODate(data?.freightPrepaid?.cshtCuocChi?.date),

      canCuocChi: convertNumber(data?.freightPrepaid?.canCuocChi?.monney),
      taxCanCuocChi: convertVat(data?.freightPrepaid?.canCuocChi?.vat),
      taxMoneyCanCuocChi: convertNumber(data?.freightPrepaid?.canCuocChi?.monneyVat),
      // invoiceCanCuocChi: data?.freightPrepaid?.canCuocChi?.invoiceNumber,
      // dateCanCuocChi: convertToISODate(data?.freightPrepaid?.cshtCuocChi?.date),

      //Chi phí khác Chi hộ
      chiPhiKhacChiHo: convertNumber(data?.freightPrepaid?.chiphikhacChiHo?.monney),
      taxChiPhiKhacChiHo: convertVat(data?.freightPrepaid?.chiphikhacChiHo?.vat),
      taxMoneyChiPhiKhacChiHo: convertNumber(data?.freightPrepaid?.chiphikhacChiHo?.monneyVat),
      //Chi phí khác thông quan
      chiPhiKhacThongQuan: convertNumber(data?.freightPrepaid?.chiphikhacThongQuan?.monney),
      taxChiPhiKhacThongQuan: convertVat(data?.freightPrepaid?.chiphikhacThongQuan?.vat),
      taxMoneyChiPhiKhacThongQuan: convertNumber(data?.freightPrepaid?.chiphikhacThongQuan?.monneyVat)
    },
    expenseTrip: isCompany
      ? {
          litDiesel: convertNumber(data?.expenseTrip?.litDiesel),
          priceDiesel: convertNumber(data?.expenseTrip?.priceDiesel),
          etc: convertNumber(data?.expenseTrip?.etc),
          noteEtc: data?.expenseTrip?.noteEtc,
          salaryTrip: convertNumber(data?.expenseTrip?.salaryTrip),
          noteSalaryTrip: data?.expenseTrip?.noteSalaryTrip,
          roadFee: convertNumber(data?.expenseTrip?.roadFee),
          noteRoadFee: data?.expenseTrip?.noteRoadFee,
          businessTrip: convertNumber(data?.expenseTrip?.businessTrip),
          noteBusinessTrip: data?.expenseTrip?.noteBusinessTrip,
          nightShift: convertNumber(data?.expenseTrip?.nightShift),
          noteNightShift: data?.expenseTrip?.noteNightShift,
          repair: convertNumber(data?.expenseTrip?.repair),
          noteRepair: data?.expenseTrip?.noteRepair,
          etcOther: convertNumber(data?.expenseTrip?.etcOther),
          noteEtcOther: data?.expenseTrip?.noteEtcOther,
          repairOther: convertNumber(data?.expenseTrip?.repairOther),
          noteRepairOther: data?.expenseTrip?.noteRepairOther,
          chiPhiKhac: convertNumber(data?.expenseTrip?.chiPhiKhac),
          noteChiPhiKhac: data?.expenseTrip?.noteChiPhiKhac,
          totalPriceDiesel: convertNumber(data?.expenseTrip?.totalPriceDiesel)
        }
      : null
  }
}

export const convertToFormValuesOrder = (data: OrderType): FormValuesOrder => {
  return {
    orderInfo: {
      noBill: data?.noBill || '',
      pic: data?.pic || '',
      noBooking: data?.noBooking || '',
      noContact: data?.noContact || '',
      noteCustomer: data?.noteCustomer || '',
      orderTypeId: data?.orderTypeId ? String(data?.orderTypeId) : '',
      customerId: data?.customerId ? String(data?.customerId) : '',
      staffSaleId: data?.staffSaleId ? String(data?.staffSaleId) : '',
      staffKHId: data?.staffKHId ? String(data?.staffKHId) : '',
      staffTDKHId: data?.staffTDKHId ? String(data?.staffTDKHId) : '',
      staffOPId: data?.staffOPId ? String(data?.staffOPId) : ''
    },
    vehicleInfo: {
      typeTransport: data?.typeTransport || 'VENDOR', // Default to 'VENDOR'
      noteCar: data?.noteCar || '',
      vendorId: data?.vendorId ? String(data?.vendorId) : '',
      driverName: data?.driverName || '',
      licensePlate: data?.licensePlate || '',
      driverPhoneNumber: data?.driverPhoneNumber || '',
      myCarId: data?.myCarId ? String(data?.myCarId) : '',
      staffDriverId: data?.staffDriverId ? String(data?.staffDriverId) : ''
    },
    goodsInfo: {
      noContainer: data?.noContainer || '',
      noSeal: data?.noSeal || '',
      weight: data?.weight || '',
      shippingLineId: data?.shippingLineId ? String(data?.shippingLineId) : '',
      containerId: data?.containerId ? String(data?.containerId) : ''
    },
    transferOrder: {
      dateStart: data?.dateStart ? dayjs(data?.dateStart).toString() : '', // Convert to string using dayjs
      dateEnd: data?.dateEnd ? dayjs(data?.dateEnd).toString() : '', // Convert to string using dayjs
      placeStart: data?.placeStart || '',
      placeEnd: data?.placeEnd || '',
      locationStartId: data?.locationStartId ?? null,
      locationEndId: data?.locationEndId ?? null,
      locationStart: data?.locationStart,
      locationEnd: data?.locationEnd
    },
    locationIns: (data?.locationIns.length > 0 &&
      data?.locationIns?.map((e: locationOrder) => ({
        id: e.id,
        place: e.place || '',
        date: e.date ? dayjs(e.date).toString() : '', // Convert to string using dayjs
        nameContact: e.nameContact || '',
        phoneContact: e.phoneContact || '',
        wareHouse: e.wareHouse || '',
        gps: e.gps || '',
        note: e.note || ''
      }))) || [DEFAULT_LOCAL_IN_OUT],
    locationOuts: (data?.locationOuts.length > 0 &&
      data?.locationOuts?.map((e: locationOrder) => ({
        id: e.id,
        place: e.place || '',
        date: e.date ? dayjs(e.date).toString() : '', // Convert to string using dayjs
        nameContact: e.nameContact || '',
        phoneContact: e.phoneContact || '',
        wareHouse: e.wareHouse || '',
        gps: e.gps || '',
        note: e.note || ''
      }))) || [DEFAULT_LOCAL_IN_OUT],
    freightCollect: {
      noteChiPhiKhacCuocThu: data?.revenue?.noteChiPhiKhacCuocThu,
      noteChiPhiKhacChiHo: data?.revenue?.noteChiPhiKhacChiHo,
      noteChiPhiKhacThongQuan: data?.revenue?.noteChiPhiKhacThongQuan,
      phiRu: {
        monney: data?.revenue.phiRu ? String(data?.revenue.phiRu) : '',
        vat: convertVatToForm(data?.revenue.taxPhiRu), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyPhiRu ? String(data?.revenue.taxMoneyPhiRu) : ''
      },
      cuoc: {
        monney: data?.revenue.cuocThu ? String(data?.revenue.cuocThu) : '',
        vat: convertVatToForm(data?.revenue.taxCuocThu), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyCuocThu ? String(data?.revenue.taxMoneyCuocThu) : ''
      },
      comReality: {
        monney: data?.revenue.comReality ? String(data?.revenue.comReality) : '',
        vat: convertVatToForm(data?.revenue.taxComReality), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyComReality ? String(data?.revenue.taxMoneyComReality) : ''
      },
      lachHuyen: {
        monney: data?.revenue.lachHuyen ? String(data?.revenue.lachHuyen) : '',
        vat: convertVatToForm(data?.revenue.taxLachHuyen), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyLachHuyen ? String(data?.revenue.taxMoneyLachHuyen) : ''
      },
      caXe: {
        monney: data?.revenue.caXe ? String(data?.revenue.caXe) : '',
        vat: convertVatToForm(data?.revenue.taxCaXe), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyCaXe ? String(data?.revenue.taxMoneyCaXe) : ''
      },
      namLai: {
        monney: data?.revenue.namLai ? String(data?.revenue.namLai) : '',
        vat: convertVatToForm(data?.revenue.taxNamLai), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyNamLai ? String(data?.revenue.taxMoneyNamLai) : ''
      },
      cshtCuocThu: {
        monney: data?.revenue.cshtCuocThu ? String(data?.revenue.cshtCuocThu) : '',
        vat: convertVatToForm(data?.revenue.taxCshtCuocThu), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyCshtCuocThu ? String(data?.revenue.taxMoneyCshtCuocThu) : ''
      },
      canCuocThu: {
        monney: data?.revenue.canCuocThu ? String(data?.revenue.canCuocThu) : '',
        vat: convertVatToForm(data?.revenue.taxCanCuocThu), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyCanCuocThu ? String(data?.revenue.taxMoneyCanCuocThu) : ''
      },
      haCuocThu: {
        monney: data?.revenue.haCuocThu ? String(data?.revenue.haCuocThu) : '',
        vat: convertVatToForm(data?.revenue.taxHaCuocThu), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyHaCuocThu ? String(data?.revenue.taxMoneyHaCuocThu) : ''
      },
      nangCuocThu: {
        monney: data?.revenue.nangCuocThu ? String(data?.revenue.nangCuocThu) : '',
        vat: convertVatToForm(data?.revenue.taxNangCuocThu), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyNangCuocThu ? String(data?.revenue.taxMoneyNangCuocThu) : ''
      },
      chiphikhacVanChuyen: {
        monney: data?.revenue.chiPhiKhac ? String(data?.revenue.chiPhiKhac) : '',
        vat: convertVatToForm(data?.revenue.taxChiPhiKhac), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyChiPhiKhac ? String(data?.revenue.taxMoneyChiPhiKhac) : ''
      },
      chiphikhacChiHo: {
        monney: data?.revenue.chiPhiKhacChiHo ? String(data?.revenue.chiPhiKhacChiHo) : '',
        vat: convertVatToForm(data?.revenue.taxChiPhiKhacChiHo), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyChiPhiKhacChiHo ? String(data?.revenue.taxMoneyChiPhiKhacChiHo) : ''
      },
      thongQuan: {
        monney: data?.revenue.thongQuan ? String(data?.revenue.thongQuan) : '',
        vat: convertVatToForm(data?.revenue.taxThongQuan), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyThongQuan ? String(data?.revenue.taxMoneyThongQuan) : ''
      },
      kyHaiQuanGiamSat: {
        monney: data?.revenue.haiQuan ? String(data?.revenue.haiQuan) : '',
        vat: convertVatToForm(data?.revenue.taxHaiQuan), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyHaiQuan ? String(data?.revenue.taxMoneyHaiQuan) : ''
      },
      chiphikhacThongQuan: {
        monney: data?.revenue.chiPhiKhacThongQuan ? String(data?.revenue.chiPhiKhacThongQuan) : '',
        vat: convertVatToForm(data?.revenue.taxChiPhiKhacThongQuan), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyChiPhiKhacThongQuan ? String(data?.revenue.taxMoneyChiPhiKhacThongQuan) : ''
      },
      kiemHoa: {
        monney: data?.revenue.kiemHoa ? String(data?.revenue.kiemHoa) : '',
        vat: convertVatToForm(data?.revenue.taxKiemHoa), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyKiemHoa ? String(data?.revenue.taxMoneyKiemHoa) : ''
      },
      kiemDich: {
        monney: data?.revenue.kiemDich ? String(data?.revenue.kiemDich) : '',
        vat: convertVatToForm(data?.revenue.taxKiemDich), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyKiemDich ? String(data?.revenue.taxMoneyKiemDich) : ''
      },
      hunTrung: {
        monney: data?.revenue.hunTrung ? String(data?.revenue.hunTrung) : '',
        vat: convertVatToForm(data?.revenue.taxHunTrung), // Default to '' if not available
        monneyVat: data?.revenue.taxMoneyHunTrung ? String(data?.revenue.taxMoneyHunTrung) : ''
      },
      coSoHaTang: {
        monney: data?.revenue.csht ? String(data?.revenue.csht) : ''
      },
      nang: {
        monney: data?.revenue.nang ? String(data?.revenue.nang) : ''
      },
      ha: {
        monney: data?.revenue.ha ? String(data?.revenue.ha) : ''
      },
      can: {
        monney: data?.revenue.can ? String(data?.revenue.can) : ''
      }
    },
    freightPrepaid: {
      noteChiPhiKhacCuocChi: data?.expense?.noteChiPhiKhacCuocChi,
      noteChiPhiKhacChiHo: data?.expense?.noteChiPhiKhacChiHo,
      noteChiPhiKhacThongQuan: data?.expense?.noteChiPhiKhacThongQuan,
      phiRu: {
        monney: data?.expense?.phiRu ? String(data?.expense?.phiRu) : '', // Default to '' if not available
        vat: convertVatToForm(data?.expense?.taxPhiRu), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyPhiRu ? String(data?.expense?.taxMoneyPhiRu) : '', // Default to '' if not available
        invoiceNumber: data?.expense?.invoicePhiRu || '', // Default to empty string if not available
        date: convertDateToForm(data?.expense?.dateInvoicePhiRu) || ''
      },
      com: {
        monney: data?.expense?.com ? String(data?.expense?.com) : '', // Default to '' if not available
        vat: convertVatToForm(data?.expense?.taxCom), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyCom ? String(data?.expense?.taxMoneyCom) : '', // Default to '' if not available
        invoiceNumber: data?.expense?.invoiceCom || '', // Default to empty string if not available
        noteCom: data?.expense?.noteCom || '' // Default to empty string if not available
      },
      cuoc: {
        monney: data?.expense?.cuocChi ? String(data?.expense?.cuocChi) : '', // Default to '' if not available
        vat: convertVatToForm(data?.expense?.taxCuocChi), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyCuocChi ? String(data?.expense?.taxMoneyCuocChi) : '', // Default to '' if not available
        invoiceNumber: data?.expense?.invoiceCuocChi || '', // Default to empty string if not available
        date: convertDateToForm(data?.expense?.dateInvoiceCuocChi) || ''
      },
      // comReality: {
      //   monney: data?.expense?.comReality ? String(data?.expense?.comReality) : '',
      //   vat: convertVatToForm(data?.expense?.taxComReality), // Default to '' if not available
      //   monneyVat: data?.expense?.taxMoneyComReality ? String(data?.expense?.taxMoneyComReality) : '',
      //   invoiceNumber: data?.expense?.invoiceComReality || ''
      // },
      lachHuyen: {
        monney: data?.expense?.lachHuyen ? String(data?.expense?.lachHuyen) : '',
        vat: convertVatToForm(data?.expense?.taxLachHuyen), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyLachHuyen ? String(data?.expense?.taxMoneyLachHuyen) : ''
        // invoiceNumber: data?.expense?.invoiceCom || '' // Default to empty string if not available
      },
      caXe: {
        monney: data?.expense?.caXe ? String(data?.expense?.caXe) : '',
        vat: convertVatToForm(data?.expense?.taxCaXe), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyCaXe ? String(data?.expense?.taxMoneyCaXe) : ''
        // invoiceNumber: data?.expense?.invoiceCom || '' // Default to empty string if not available
      },
      namLai: {
        monney: data?.expense?.namLai ? String(data?.expense?.namLai) : '',
        vat: convertVatToForm(data?.expense?.taxNamLai), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyNamLai ? String(data?.expense?.taxMoneyNamLai) : ''
        // invoiceNumber: data?.expense?.invoiceCom || '' // Default to empty string if not available
      },
      cshtCuocChi: {
        monney: data?.expense?.cshtCuocChi ? String(data?.expense.cshtCuocChi) : '',
        vat: convertVatToForm(data?.expense?.taxCshtCuocChi), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyCshtCuocChi ? String(data?.expense.taxMoneyCshtCuocChi) : ''
      },
      canCuocChi: {
        monney: data?.expense?.canCuocChi ? String(data?.expense.canCuocChi) : '',
        vat: convertVatToForm(data?.expense?.taxCanCuocChi), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyCanCuocChi ? String(data?.expense.taxMoneyCanCuocChi) : ''
      },
      haCuocChi: {
        monney: data?.expense?.haCuocChi ? String(data?.expense.haCuocChi) : '',
        vat: convertVatToForm(data?.expense?.taxHaCuocChi), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyHaCuocChi ? String(data?.expense.taxMoneyHaCuocChi) : ''
      },
      nangCuocChi: {
        monney: data?.expense?.nangCuocChi ? String(data?.expense.nangCuocChi) : '',
        vat: convertVatToForm(data?.expense?.taxNangCuocChi), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyNangCuocChi ? String(data?.expense.taxMoneyNangCuocChi) : ''
      },
      chiphikhacVanChuyen: {
        monney: data?.expense?.chiPhiKhac ? String(data?.expense?.chiPhiKhac) : '',
        vat: convertVatToForm(data?.expense?.taxChiPhiKhac), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyChiPhiKhac ? String(data?.expense?.taxMoneyChiPhiKhac) : ''
        // invoiceNumber: data?.expense?.invoiceCom || '' // Default to empty string if not available
      },
      chiphikhacChiHo: {
        monney: data?.expense?.chiPhiKhacChiHo ? String(data?.expense?.chiPhiKhacChiHo) : '',
        vat: convertVatToForm(data?.expense?.taxChiPhiKhacChiHo), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyChiPhiKhacChiHo ? String(data?.expense?.taxMoneyChiPhiKhacChiHo) : ''
        // invoiceNumber: data?.expense?.invoiceCom || '' // Default to empty string if not available
      },
      thongQuan: {
        monney: data?.expense?.thongQuan ? String(data?.expense?.thongQuan) : '',
        vat: convertVatToForm(data?.expense?.taxThongQuan), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyThongQuan ? String(data?.expense?.taxMoneyThongQuan) : '',
        invoiceNumber: data?.expense?.invoiceThongQuan || '', // Default to empty string if not available
        date: convertDateToForm(data?.expense?.dateInvoiceThongQuan) || ''
      },
      chiphikhacThongQuan: {
        monney: data?.expense?.chiPhiKhacThongQuan ? String(data?.expense?.chiPhiKhacThongQuan) : '',
        vat: convertVatToForm(data?.expense?.taxChiPhiKhacThongQuan), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyChiPhiKhacThongQuan ? String(data?.expense?.taxMoneyChiPhiKhacThongQuan) : ''
      },
      kyHaiQuanGiamSat: {
        monney: data?.expense?.haiQuan ? String(data?.expense?.haiQuan) : '',
        vat: convertVatToForm(data?.expense?.taxHaiQuan), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyHaiQuan ? String(data?.expense?.taxMoneyHaiQuan) : '',
        invoiceNumber: data?.expense?.invoiceHaiQuan || '', // Default to empty string if not available
        date: convertDateToForm(data?.expense?.dateInvoiceHaiQuan) || ''
      },
      kiemHoa: {
        monney: data?.expense?.kiemHoa ? String(data?.expense?.kiemHoa) : '',
        vat: convertVatToForm(data?.expense?.taxKiemHoa), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyKiemHoa ? String(data?.expense?.taxMoneyKiemHoa) : '',
        invoiceNumber: data?.expense?.invoiceKiemHoa || '', // Default to empty string if not available
        date: convertDateToForm(data?.expense?.dateInvoiceKiemHoa) || ''
      },
      kiemDich: {
        monney: data?.expense?.kiemDich ? String(data?.expense?.kiemDich) : '',
        vat: convertVatToForm(data?.expense?.taxKiemDich), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyKiemDich ? String(data?.expense?.taxMoneyKiemDich) : '',
        invoiceNumber: data?.expense?.invoiceKiemDich || '', // Default to empty string if not available
        date: convertDateToForm(data?.expense?.dateInvoiceKiemDich) || ''
      },
      hunTrung: {
        monney: data?.expense?.hunTrung ? String(data?.expense?.hunTrung) : '',
        vat: convertVatToForm(data?.expense?.taxHunTrung), // Default to '' if not available
        monneyVat: data?.expense?.taxMoneyHunTrung ? String(data?.expense?.taxMoneyHunTrung) : '',
        invoiceNumber: data?.expense?.invoiceHunTrung || '', // Default to empty string if not available
        date: convertDateToForm(data?.expense?.dateInvoiceHunTrung) || ''
      },
      coSoHaTang: {
        monney: data?.expense?.csht ? String(data?.expense?.csht) : '',
        invoiceNumber: data?.expense?.invoiceCSHT || '' // Default to empty string if not available
      },
      nang: {
        monney: data?.expense?.nang ? String(data?.expense?.nang) : '',
        invoiceNumber: data?.expense?.invoiceNang || '' // Default to empty string if not available
      },
      ha: {
        monney: data?.expense?.ha ? String(data?.expense?.ha) : '',
        invoiceNumber: data?.expense?.invoiceHa || '' // Default to empty string if not available
      },
      can: {
        monney: data?.expense?.can ? String(data?.expense?.can) : '',
        invoiceNumber: data?.expense?.invoiceCan || '' // Default to empty string if not available
      }
    },
    expenseTrip: {
      litDiesel: String(data?.expenseTrip?.litDiesel || ''),
      priceDiesel: String(data?.expenseTrip?.priceDiesel || ''),
      etc: String(data?.expenseTrip?.etc || ''),
      noteEtc: data?.expenseTrip?.noteEtc,
      salaryTrip: String(data?.expenseTrip?.salaryTrip || ''),
      noteSalaryTrip: data?.expenseTrip?.noteSalaryTrip,
      roadFee: String(data?.expenseTrip?.roadFee || ''),
      noteRoadFee: data?.expenseTrip?.noteRoadFee,
      businessTrip: String(data?.expenseTrip?.businessTrip || ''),
      noteBusinessTrip: data?.expenseTrip?.noteBusinessTrip,
      nightShift: String(data?.expenseTrip?.nightShift || ''),
      noteNightShift: data?.expenseTrip?.noteNightShift,
      repair: String(data?.expenseTrip?.repair || ''),
      noteRepair: data?.expenseTrip?.noteRepair,
      etcOther: String(data?.expenseTrip?.etcOther || ''),
      noteEtcOther: data?.expenseTrip?.noteEtcOther,
      repairOther: String(data?.expenseTrip?.repairOther || ''),
      noteRepairOther: data?.expenseTrip?.noteRepairOther,
      chiPhiKhac: String(data?.expenseTrip?.chiPhiKhac || ''),
      noteChiPhiKhac: data?.expenseTrip?.noteChiPhiKhac,
      totalPriceDiesel: String(Number(data?.expenseTrip?.litDiesel || 0) + Number(data?.expenseTrip?.priceDiesel || 0))
    }
  }
}

export function normalizeTaxRate(taxRate: string | number) {
  if (taxRate === 'KHĐ') {
    return 0
  }
  const num = Number(taxRate)
  return isNaN(num) ? 0 : num
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export const highlightChangesFields = (orderChanges: orderChange[], startsWith: string) =>
  orderChanges?.reduce((acc: any, { fieldName }) => {
    if (fieldName.startsWith(startsWith)) {
      const key = fieldName.split('.')[1]
      acc[key] = true
    }
    return acc
  }, {})
/* eslint-enable @typescript-eslint/no-explicit-any */

//Lô hàng
export const formatDataSubmitShipment = (data: FormValuesOrderShipment) => {
  return {
    reasonUpdate: data?.reasonUpdate,
    pic: data?.pic,
    noBill: data?.noBill,
    noBooking: data?.noBooking,
    orderTypeId: convertNumber(data?.orderTypeId), // id loại hình vận chuyển
    customerId: convertNumber(data?.customerId), //id khách hàng
    staffSaleId: convertNumber(data?.staffSaleId), //nhân viên sale
    staffKHId: convertNumber(data?.staffKHId), // nhân viên kế hoạch
    staffTDKHId: convertNumber(data?.staffTDKHId), // nhân viên theo dõi kế hoạch
    staffOPId: convertNumber(data?.staffOPId), // nhân viên hiện trường
    revenue: {
      noteChiPhiKhacCuocThu: data?.freightCollect?.noteChiPhiKhacCuocThu,
      noteChiPhiKhacChiHo: data?.freightCollect?.noteChiPhiKhacChiHo,
      noteChiPhiKhacThongQuan: data?.freightCollect?.noteChiPhiKhacThongQuan,
      phiRu: convertNumber(data?.freightCollect?.phiRu?.monney),
      taxPhiRu: convertVat(data?.freightCollect?.phiRu?.vat),
      taxMoneyPhiRu: convertNumber(data?.freightCollect?.phiRu?.monneyVat),
      cuocThu: convertNumber(data?.freightCollect?.cuoc?.monney),
      taxCuocThu: convertVat(data?.freightCollect?.cuoc?.vat),
      taxMoneyCuocThu: convertNumber(data?.freightCollect?.cuoc?.monneyVat),
      comReality: convertNumber(data?.freightCollect?.comReality?.monney),
      taxComReality: convertVat(data?.freightCollect?.comReality?.vat),
      taxMoneyComReality: convertNumber(data?.freightCollect?.comReality?.monneyVat),
      lachHuyen: convertNumber(data?.freightCollect?.lachHuyen?.monney),
      taxLachHuyen: convertVat(data?.freightCollect?.lachHuyen?.vat),
      taxMoneyLachHuyen: convertNumber(data?.freightCollect?.lachHuyen?.monneyVat),
      caXe: convertNumber(data?.freightCollect?.caXe?.monney),
      taxCaXe: convertVat(data?.freightCollect?.caXe?.vat),
      taxMoneyCaXe: convertNumber(data?.freightCollect?.caXe?.monneyVat),
      namLai: convertNumber(data?.freightCollect?.namLai?.monney),
      taxNamLai: convertVat(data?.freightCollect?.namLai?.vat),
      taxMoneyNamLai: convertNumber(data?.freightCollect?.namLai?.monneyVat),

      cshtCuocThu: convertNumber(data?.freightCollect?.cshtCuocThu?.monney),
      taxCshtCuocThu: convertVat(data?.freightCollect?.cshtCuocThu?.vat),
      taxMoneyCshtCuocThu: convertNumber(data?.freightCollect?.cshtCuocThu?.monneyVat),

      nangCuocThu: convertNumber(data?.freightCollect?.nangCuocThu?.monney),
      taxNangCuocThu: convertVat(data?.freightCollect?.nangCuocThu?.vat),
      taxMoneyNangCuocThu: convertNumber(data?.freightCollect?.nangCuocThu?.monneyVat),

      haCuocThu: convertNumber(data?.freightCollect?.haCuocThu?.monney),
      taxHaCuocThu: convertVat(data?.freightCollect?.haCuocThu?.vat),
      taxMoneyHaCuocThu: convertNumber(data?.freightCollect?.haCuocThu?.monneyVat),

      canCuocThu: convertNumber(data?.freightCollect?.canCuocThu?.monney),
      taxCanCuocThu: convertVat(data?.freightCollect?.canCuocThu?.vat),
      taxMoneyCanCuocThu: convertNumber(data?.freightCollect?.canCuocThu?.monneyVat),

      //Chi phí khác vận chuyển
      chiPhiKhac: convertNumber(data?.freightCollect?.chiphikhacVanChuyen?.monney),
      taxChiPhiKhac: convertVat(data?.freightCollect?.chiphikhacVanChuyen?.vat),
      taxMoneyChiPhiKhac: convertNumber(data?.freightCollect?.chiphikhacVanChuyen?.monneyVat),
      //Chi phí khác Chi hộ
      chiphikhacChiHo: convertNumber(data?.freightCollect?.chiphikhacChiHo?.monney),
      taxChiPhiKhacChiHo: convertVat(data?.freightCollect?.chiphikhacChiHo?.vat),
      taxMoneyChiPhiKhacChiHo: convertNumber(data?.freightCollect?.chiphikhacChiHo?.monneyVat),
      //Chi phí khác thông quan
      chiPhiKhacThongQuan: convertNumber(data?.freightCollect?.chiphikhacThongQuan?.monney),
      taxChiPhiKhacThongQuan: convertVat(data?.freightCollect?.chiphikhacThongQuan?.vat),
      taxMoneyChiPhiKhacThongQuan: convertNumber(data?.freightCollect?.chiphikhacThongQuan?.monneyVat),
      //
      thongQuan: convertNumber(data?.freightCollect?.thongQuan?.monney),
      taxThongQuan: convertVat(data?.freightCollect?.thongQuan?.vat),
      taxMoneyThongQuan: convertNumber(data?.freightCollect?.thongQuan?.monneyVat),
      haiQuan: convertNumber(data?.freightCollect?.kyHaiQuanGiamSat?.monney),
      taxHaiQuan: convertVat(data?.freightCollect?.kyHaiQuanGiamSat?.vat),
      taxMoneyHaiQuan: convertNumber(data?.freightCollect?.kyHaiQuanGiamSat?.monneyVat),
      kiemHoa: convertNumber(data?.freightCollect?.kiemHoa?.monney),
      taxKiemHoa: convertVat(data?.freightCollect?.kiemHoa?.vat),
      taxMoneyKiemHoa: convertNumber(data?.freightCollect?.kiemHoa?.monneyVat),
      kiemDich: convertNumber(data?.freightCollect?.kiemDich?.monney),
      taxKiemDich: convertVat(data?.freightCollect?.kiemDich?.vat),
      taxMoneyKiemDich: convertNumber(data?.freightCollect?.kiemDich?.monneyVat),
      hunTrung: convertNumber(data?.freightCollect?.hunTrung?.monney),
      taxHunTrung: convertVat(data?.freightCollect?.hunTrung?.vat),
      taxMoneyHunTrung: convertNumber(data?.freightCollect?.hunTrung?.monneyVat),
      csht: convertNumber(data?.freightCollect?.coSoHaTang?.monney),
      nang: convertNumber(data?.freightCollect?.nang?.monney),
      ha: convertNumber(data?.freightCollect?.ha?.monney),
      can: convertNumber(data?.freightCollect?.can?.monney)
    },
    expense: {
      noteChiPhiKhacCuocChi: data?.freightPrepaid?.noteChiPhiKhacCuocChi,
      noteChiPhiKhacChiHo: data?.freightPrepaid?.noteChiPhiKhacChiHo,
      noteChiPhiKhacThongQuan: data?.freightPrepaid?.noteChiPhiKhacThongQuan,
      phiRu: convertNumber(data?.freightPrepaid?.phiRu?.monney),
      taxPhiRu: convertVat(data?.freightPrepaid?.phiRu?.vat),
      taxMoneyPhiRu: convertNumber(data?.freightPrepaid?.phiRu?.monneyVat),
      invoicePhiRu: data?.freightPrepaid?.phiRu?.invoiceNumber,
      dateInvoicePhiRu: convertToISODate(data?.freightPrepaid?.phiRu?.date),
      com: convertNumber(data?.freightPrepaid?.com?.monney),
      taxCom: convertVat(data?.freightPrepaid?.com?.vat),
      taxMoneyCom: convertNumber(data?.freightPrepaid?.com?.monneyVat),
      invoiceCom: data?.freightPrepaid?.com?.invoiceNumber,
      noteCom: data?.freightPrepaid?.com?.noteCom,
      cuocChi: convertNumber(data?.freightPrepaid?.cuoc?.monney),
      taxCuocChi: convertVat(data?.freightPrepaid?.cuoc?.vat),
      taxMoneyCuocChi: convertNumber(data?.freightPrepaid?.cuoc?.monneyVat),
      invoiceCuocChi: data?.freightPrepaid?.cuoc?.invoiceNumber,
      dateInvoiceCuocChi: convertToISODate(data?.freightPrepaid?.cuoc?.date),
      // comReality: convertNumber(data?.freightPrepaid?.comReality?.monney),
      // taxComReality: convertVat(data?.freightPrepaid?.comReality?.vat),
      // taxMoneyComReality: convertNumber(data?.freightPrepaid?.comReality?.monneyVat),
      // invoiceComReality: data?.freightPrepaid?.comReality?.invoiceNumber,
      lachHuyen: convertNumber(data?.freightPrepaid?.lachHuyen?.monney),
      taxLachHuyen: convertVat(data?.freightPrepaid?.lachHuyen?.vat),
      taxMoneyLachHuyen: convertNumber(data?.freightPrepaid?.lachHuyen?.monneyVat),
      caXe: convertNumber(data?.freightPrepaid?.caXe?.monney),
      taxCaXe: convertVat(data?.freightPrepaid?.caXe?.vat),
      taxMoneyCaXe: convertNumber(data?.freightPrepaid?.caXe?.monneyVat),
      namLai: convertNumber(data?.freightPrepaid?.namLai?.monney),
      taxNamLai: convertVat(data?.freightPrepaid?.namLai?.vat),
      taxMoneyNamLai: convertNumber(data?.freightPrepaid?.namLai?.monneyVat),
      chiPhiKhac: convertNumber(data?.freightPrepaid?.chiphikhacVanChuyen?.monney),
      taxChiPhiKhac: convertVat(data?.freightPrepaid?.chiphikhacVanChuyen?.vat),
      taxMoneyChiPhiKhac: convertNumber(data?.freightPrepaid?.chiphikhacVanChuyen?.monneyVat),
      thongQuan: convertNumber(data?.freightPrepaid?.thongQuan?.monney),
      taxThongQuan: convertVat(data?.freightPrepaid?.thongQuan?.vat),
      dateInvoiceThongQuan: convertToISODate(data?.freightPrepaid?.thongQuan?.date),
      taxMoneyThongQuan: convertNumber(data?.freightPrepaid?.thongQuan?.monneyVat),
      invoiceThongQuan: data?.freightPrepaid?.thongQuan?.invoiceNumber,
      haiQuan: convertNumber(data?.freightPrepaid?.kyHaiQuanGiamSat?.monney),
      taxHaiQuan: convertVat(data?.freightPrepaid?.kyHaiQuanGiamSat?.vat),
      taxMoneyHaiQuan: convertNumber(data?.freightPrepaid?.kyHaiQuanGiamSat?.monneyVat),
      invoiceHaiQuan: data?.freightPrepaid?.kyHaiQuanGiamSat?.invoiceNumber,
      dateInvoiceHaiQuan: convertToISODate(data?.freightPrepaid?.kyHaiQuanGiamSat?.date),
      kiemHoa: convertNumber(data?.freightPrepaid?.kiemHoa?.monney),
      taxKiemHoa: convertVat(data?.freightPrepaid?.kiemHoa?.vat),
      taxMoneyKiemHoa: convertNumber(data?.freightPrepaid?.kiemHoa?.monneyVat),
      invoiceKiemHoa: data?.freightPrepaid?.kiemHoa?.invoiceNumber,
      dateInvoiceKiemHoa: convertToISODate(data?.freightPrepaid?.kiemHoa?.date),
      kiemDich: convertNumber(data?.freightPrepaid?.kiemDich?.monney),
      taxKiemDich: convertVat(data?.freightPrepaid?.kiemDich?.vat),
      taxMoneyKiemDich: convertNumber(data?.freightPrepaid?.kiemDich?.monneyVat),
      invoiceKiemDich: data?.freightPrepaid?.kiemDich?.invoiceNumber,
      dateInvoiceKiemDich: convertToISODate(data?.freightPrepaid?.kiemDich?.date),
      hunTrung: convertNumber(data?.freightPrepaid?.hunTrung?.monney),
      taxHunTrung: convertVat(data?.freightPrepaid?.hunTrung?.vat),
      taxMoneyHunTrung: convertNumber(data?.freightPrepaid?.hunTrung?.monneyVat),
      invoiceHunTrung: data?.freightPrepaid?.hunTrung?.invoiceNumber,
      dateInvoiceHunTrung: convertToISODate(data?.freightPrepaid?.hunTrung?.date),
      csht: convertNumber(data?.freightPrepaid?.coSoHaTang?.monney),
      invoiceCSHT: data?.freightPrepaid?.coSoHaTang?.invoiceNumber,
      nang: convertNumber(data?.freightPrepaid?.nang?.monney),
      invoiceNang: data?.freightPrepaid?.nang?.invoiceNumber,
      ha: convertNumber(data?.freightPrepaid?.ha?.monney),
      invoiceHa: data?.freightPrepaid?.ha?.invoiceNumber,
      can: convertNumber(data?.freightPrepaid?.can?.monney),
      invoiceCan: data?.freightPrepaid?.can?.invoiceNumber,

      cshtCuocChi: convertNumber(data?.freightPrepaid?.cshtCuocChi?.monney),
      taxCshtCuocChi: convertVat(data?.freightPrepaid?.cshtCuocChi?.vat),
      taxMoneyCshtCuocChi: convertNumber(data?.freightPrepaid?.cshtCuocChi?.monneyVat),
      // invoiceCshtCuocChi: data?.freightPrepaid?.cshtCuocChi?.invoiceNumber,
      // dateCshtCuocChi: convertDate(data?.freightPrepaid?.cshtCuocChi?.date),

      nangCuocChi: convertNumber(data?.freightPrepaid?.nangCuocChi?.monney),
      taxNangCuocChi: convertVat(data?.freightPrepaid?.nangCuocChi?.vat),
      taxMoneyNangCuocChi: convertNumber(data?.freightPrepaid?.nangCuocChi?.monneyVat),
      // invoiceNangCuocChi: data?.freightPrepaid?.nangCuocChi?.invoiceNumber,
      // dateNangCuocChi: convertDate(data?.freightPrepaid?.cshtCuocChi?.date),

      haCuocChi: convertNumber(data?.freightPrepaid?.haCuocChi?.monney),
      taxHaCuocChi: convertVat(data?.freightPrepaid?.haCuocChi?.vat),
      taxMoneyHaCuocChi: convertNumber(data?.freightPrepaid?.haCuocChi?.monneyVat),

      //Chi phí khác Chi hộ
      chiPhiKhacChiHo: convertNumber(data?.freightPrepaid?.chiphikhacChiHo?.monney),
      taxChiPhiKhacChiHo: convertVat(data?.freightPrepaid?.chiphikhacChiHo?.vat),
      taxMoneyChiPhiKhacChiHo: convertNumber(data?.freightPrepaid?.chiphikhacChiHo?.monneyVat),
      //Chi phí khác thông quan
      chiPhiKhacThongQuan: convertNumber(data?.freightPrepaid?.chiphikhacThongQuan?.monney),
      taxChiPhiKhacThongQuan: convertVat(data?.freightPrepaid?.chiphikhacThongQuan?.vat),
      taxMoneyChiPhiKhacThongQuan: convertNumber(data?.freightPrepaid?.chiphikhacThongQuan?.monneyVat)
    }
  }
}

export const convertFreightCollect = (data: ShipmentType | DetailTotalCostGroupOrder) => {
  return {
    noteChiPhiKhacCuocThu: data?.revenue?.noteChiPhiKhacCuocThu,
    noteChiPhiKhacChiHo: data?.revenue?.noteChiPhiKhacChiHo,
    noteChiPhiKhacThongQuan: data?.revenue?.noteChiPhiKhacThongQuan,
    phiRu: {
      monney: data?.revenue.phiRu ? String(data?.revenue.phiRu) : '',
      vat: convertVatToForm(data?.revenue.taxPhiRu), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyPhiRu ? String(data?.revenue.taxMoneyPhiRu) : ''
    },
    cuoc: {
      monney: data?.revenue.cuocThu ? String(data?.revenue.cuocThu) : '',
      vat: convertVatToForm(data?.revenue.taxCuocThu), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyCuocThu ? String(data?.revenue.taxMoneyCuocThu) : ''
    },
    comReality: {
      monney: data?.revenue.comReality ? String(data?.revenue.comReality) : '',
      vat: convertVatToForm(data?.revenue.taxComReality), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyComReality ? String(data?.revenue.taxMoneyComReality) : ''
    },
    lachHuyen: {
      monney: data?.revenue.lachHuyen ? String(data?.revenue.lachHuyen) : '',
      vat: convertVatToForm(data?.revenue.taxLachHuyen), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyLachHuyen ? String(data?.revenue.taxMoneyLachHuyen) : ''
    },
    caXe: {
      monney: data?.revenue.caXe ? String(data?.revenue.caXe) : '',
      vat: convertVatToForm(data?.revenue.taxCaXe), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyCaXe ? String(data?.revenue.taxMoneyCaXe) : ''
    },
    namLai: {
      monney: data?.revenue.namLai ? String(data?.revenue.namLai) : '',
      vat: convertVatToForm(data?.revenue.taxNamLai), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyNamLai ? String(data?.revenue.taxMoneyNamLai) : ''
    },
    cshtCuocThu: {
      monney: data?.revenue.cshtCuocThu ? String(data?.revenue.cshtCuocThu) : '',
      vat: convertVatToForm(data?.revenue.taxCshtCuocThu), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyCshtCuocThu ? String(data?.revenue.taxMoneyCshtCuocThu) : ''
    },
    canCuocThu: {
      monney: data?.revenue.canCuocThu ? String(data?.revenue.canCuocThu) : '',
      vat: convertVatToForm(data?.revenue.taxCanCuocThu), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyCanCuocThu ? String(data?.revenue.taxMoneyCanCuocThu) : ''
    },
    haCuocThu: {
      monney: data?.revenue.haCuocThu ? String(data?.revenue.haCuocThu) : '',
      vat: convertVatToForm(data?.revenue.taxHaCuocThu), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyHaCuocThu ? String(data?.revenue.taxMoneyHaCuocThu) : ''
    },
    nangCuocThu: {
      monney: data?.revenue.nangCuocThu ? String(data?.revenue.nangCuocThu) : '',
      vat: convertVatToForm(data?.revenue.taxNangCuocThu), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyNangCuocThu ? String(data?.revenue.taxMoneyNangCuocThu) : ''
    },
    chiphikhacVanChuyen: {
      monney: data?.revenue.chiPhiKhac ? String(data?.revenue.chiPhiKhac) : '',
      vat: convertVatToForm(data?.revenue.taxChiPhiKhac), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyChiPhiKhac ? String(data?.revenue.taxMoneyChiPhiKhac) : ''
    },
    chiphikhacChiHo: {
      monney: data?.revenue.chiPhiKhacChiHo ? String(data?.revenue.chiPhiKhacChiHo) : '',
      vat: convertVatToForm(data?.revenue.taxChiPhiKhacChiHo), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyChiPhiKhacChiHo ? String(data?.revenue.taxMoneyChiPhiKhacChiHo) : ''
    },
    thongQuan: {
      monney: data?.revenue.thongQuan ? String(data?.revenue.thongQuan) : '',
      vat: convertVatToForm(data?.revenue.taxThongQuan), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyThongQuan ? String(data?.revenue.taxMoneyThongQuan) : ''
    },
    kyHaiQuanGiamSat: {
      monney: data?.revenue.haiQuan ? String(data?.revenue.haiQuan) : '',
      vat: convertVatToForm(data?.revenue.taxHaiQuan), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyHaiQuan ? String(data?.revenue.taxMoneyHaiQuan) : ''
    },
    chiphikhacThongQuan: {
      monney: data?.revenue.chiPhiKhacThongQuan ? String(data?.revenue.chiPhiKhacThongQuan) : '',
      vat: convertVatToForm(data?.revenue.taxChiPhiKhacThongQuan), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyChiPhiKhacThongQuan ? String(data?.revenue.taxMoneyChiPhiKhacThongQuan) : ''
    },
    kiemHoa: {
      monney: data?.revenue.kiemHoa ? String(data?.revenue.kiemHoa) : '',
      vat: convertVatToForm(data?.revenue.taxKiemHoa), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyKiemHoa ? String(data?.revenue.taxMoneyKiemHoa) : ''
    },
    kiemDich: {
      monney: data?.revenue.kiemDich ? String(data?.revenue.kiemDich) : '',
      vat: convertVatToForm(data?.revenue.taxKiemDich), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyKiemDich ? String(data?.revenue.taxMoneyKiemDich) : ''
    },
    hunTrung: {
      monney: data?.revenue.hunTrung ? String(data?.revenue.hunTrung) : '',
      vat: convertVatToForm(data?.revenue.taxHunTrung), // Default to '' if not available
      monneyVat: data?.revenue.taxMoneyHunTrung ? String(data?.revenue.taxMoneyHunTrung) : ''
    },
    coSoHaTang: {
      monney: data?.revenue.csht ? String(data?.revenue.csht) : ''
    },
    nang: {
      monney: data?.revenue.nang ? String(data?.revenue.nang) : ''
    },
    ha: {
      monney: data?.revenue.ha ? String(data?.revenue.ha) : ''
    },
    can: {
      monney: data?.revenue.can ? String(data?.revenue.can) : ''
    }
  }
}

export const convertFreightPrepaid = (data: ShipmentType | DetailTotalCostGroupOrder) => {
  return {
    noteChiPhiKhacCuocChi: data?.expense?.noteChiPhiKhacCuocChi,
    noteChiPhiKhacChiHo: data?.expense?.noteChiPhiKhacChiHo,
    noteChiPhiKhacThongQuan: data?.expense?.noteChiPhiKhacThongQuan,
    phiRu: {
      monney: data?.expense?.phiRu ? String(data?.expense?.phiRu) : '', // Default to '' if not available
      vat: convertVatToForm(data?.expense?.taxPhiRu), // Default to '' if not available
      monneyVat: data?.expense?.taxMoneyPhiRu ? String(data?.expense?.taxMoneyPhiRu) : '', // Default to '' if not available
      invoiceNumber: data?.expense?.invoicePhiRu || '', // Default to empty string if not available
      date: convertDateToForm(data?.expense?.dateInvoicePhiRu) || ''
    },
    com: {
      monney: data?.expense?.com ? String(data?.expense?.com) : '', // Default to '' if not available
      vat: convertVatToForm(data?.expense?.taxCom), // Default to '' if not available
      monneyVat: data?.expense?.taxMoneyCom ? String(data?.expense?.taxMoneyCom) : '', // Default to '' if not available
      invoiceNumber: data?.expense?.invoiceCom || '', // Default to empty string if not available
      noteCom: data?.expense?.noteCom || ''
    },
    cuoc: {
      monney: data?.expense?.cuocChi ? String(data?.expense?.cuocChi) : '', // Default to '' if not available
      vat: convertVatToForm(data?.expense?.taxCuocChi), // Default to '' if not available
      monneyVat: data?.expense?.taxMoneyCuocChi ? String(data?.expense?.taxMoneyCuocChi) : '', // Default to '' if not available
      invoiceNumber: data?.expense?.invoiceCuocChi || '', // Default to empty string if not available
      date: convertDateToForm(data?.expense?.dateInvoiceCuocChi) || ''
    },
    // comReality: {
    //   monney: data?.expense?.comReality ? String(data?.expense?.comReality) : '',
    //   vat: convertVatToForm(data?.expense?.taxComReality), // Default to '' if not available
    //   monneyVat: data?.expense?.taxMoneyComReality ? String(data?.expense?.taxMoneyComReality) : '',
    //   invoiceNumber: data?.expense?.invoiceComReality || ''
    // },
    lachHuyen: {
      monney: data?.expense?.lachHuyen ? String(data?.expense?.lachHuyen) : '',
      vat: convertVatToForm(data?.expense?.taxLachHuyen), // Default to '' if not available
      monneyVat: data?.expense?.taxMoneyLachHuyen ? String(data?.expense?.taxMoneyLachHuyen) : ''
      // invoiceNumber: data?.expense?.invoiceCom || '' // Default to empty string if not available
    },
    caXe: {
      monney: data?.expense?.caXe ? String(data?.expense?.caXe) : '',
      vat: convertVatToForm(data?.expense?.taxCaXe), // Default to '' if not available
      monneyVat: data?.expense?.taxMoneyCaXe ? String(data?.expense?.taxMoneyCaXe) : ''
      // invoiceNumber: data?.expense?.invoiceCom || '' // Default to empty string if not available
    },
    namLai: {
      monney: data?.expense?.namLai ? String(data?.expense?.namLai) : '',
      vat: convertVatToForm(data?.expense?.taxNamLai), // Default to '' if not available
      monneyVat: data?.expense?.taxMoneyNamLai ? String(data?.expense?.taxMoneyNamLai) : ''
      // invoiceNumber: data?.expense?.invoiceCom || '' // Default to empty string if not available
    },
    cshtCuocChi: {
      monney: data?.expense?.cshtCuocChi ? String(data?.expense.cshtCuocChi) : '',
      vat: convertVatToForm(data?.expense?.taxCshtCuocChi), // Default to '' if not available
      monneyVat: data?.expense.taxMoneyCshtCuocChi ? String(data?.expense.taxMoneyCshtCuocChi) : ''
    },
    canCuocChi: {
      monney: data?.expense.canCuocChi ? String(data?.expense.canCuocChi) : '',
      vat: convertVatToForm(data?.expense.taxCanCuocChi), // Default to '' if not available
      monneyVat: data?.expense.taxMoneyCanCuocChi ? String(data?.expense.taxMoneyCanCuocChi) : ''
    },
    haCuocChi: {
      monney: data?.expense.haCuocChi ? String(data?.expense.haCuocChi) : '',
      vat: convertVatToForm(data?.expense.taxHaCuocChi), // Default to '' if not available
      monneyVat: data?.expense.taxMoneyHaCuocChi ? String(data?.expense.taxMoneyHaCuocChi) : ''
    },
    nangCuocChi: {
      monney: data?.expense.nangCuocChi ? String(data?.expense.nangCuocChi) : '',
      vat: convertVatToForm(data?.expense.taxNangCuocChi), // Default to '' if not available
      monneyVat: data?.expense.taxMoneyNangCuocChi ? String(data?.expense.taxMoneyNangCuocChi) : ''
    },
    chiphikhacVanChuyen: {
      monney: data?.expense?.chiPhiKhac ? String(data?.expense?.chiPhiKhac) : '',
      vat: convertVatToForm(data?.expense?.taxChiPhiKhac), // Default to '' if not available
      monneyVat: data?.expense?.taxMoneyChiPhiKhac ? String(data?.expense?.taxMoneyChiPhiKhac) : ''
      // invoiceNumber: data?.expense?.invoiceCom || '' // Default to empty string if not available
    },
    chiphikhacChiHo: {
      monney: data?.expense?.chiPhiKhacChiHo ? String(data?.expense?.chiPhiKhacChiHo) : '',
      vat: convertVatToForm(data?.expense?.taxChiPhiKhacChiHo), // Default to '' if not available
      monneyVat: data?.expense?.taxMoneyChiPhiKhacChiHo ? String(data?.expense?.taxMoneyChiPhiKhacChiHo) : ''
      // invoiceNumber: data?.expense?.invoiceCom || '' // Default to empty string if not available
    },
    thongQuan: {
      monney: data?.expense?.thongQuan ? String(data?.expense?.thongQuan) : '',
      vat: convertVatToForm(data?.expense?.taxThongQuan), // Default to '' if not available
      monneyVat: data?.expense?.taxMoneyThongQuan ? String(data?.expense?.taxMoneyThongQuan) : '',
      invoiceNumber: data?.expense?.invoiceThongQuan || '', // Default to empty string if not available
      date: convertDateToForm(data?.expense?.dateInvoiceThongQuan) || ''
    },
    chiphikhacThongQuan: {
      monney: data?.expense?.chiPhiKhacThongQuan ? String(data?.expense?.chiPhiKhacThongQuan) : '',
      vat: convertVatToForm(data?.expense?.taxChiPhiKhacThongQuan), // Default to '' if not available
      monneyVat: data?.expense?.taxMoneyChiPhiKhacThongQuan ? String(data?.expense?.taxMoneyChiPhiKhacThongQuan) : ''
    },
    kyHaiQuanGiamSat: {
      monney: data?.expense?.haiQuan ? String(data?.expense?.haiQuan) : '',
      vat: convertVatToForm(data?.expense?.taxHaiQuan), // Default to '' if not available
      monneyVat: data?.expense?.taxMoneyHaiQuan ? String(data?.expense?.taxMoneyHaiQuan) : '',
      invoiceNumber: data?.expense?.invoiceHaiQuan || '', // Default to empty string if not available
      date: convertDateToForm(data?.expense?.dateInvoiceHaiQuan) || ''
    },
    kiemHoa: {
      monney: data?.expense?.kiemHoa ? String(data?.expense?.kiemHoa) : '',
      vat: convertVatToForm(data?.expense?.taxKiemHoa), // Default to '' if not available
      monneyVat: data?.expense?.taxMoneyKiemHoa ? String(data?.expense?.taxMoneyKiemHoa) : '',
      invoiceNumber: data?.expense?.invoiceKiemHoa || '', // Default to empty string if not available
      date: convertDateToForm(data?.expense?.dateInvoiceKiemHoa) || ''
    },
    kiemDich: {
      monney: data?.expense?.kiemDich ? String(data?.expense?.kiemDich) : '',
      vat: convertVatToForm(data?.expense?.taxKiemDich), // Default to '' if not available
      monneyVat: data?.expense?.taxMoneyKiemDich ? String(data?.expense?.taxMoneyKiemDich) : '',
      invoiceNumber: data?.expense?.invoiceKiemDich || '', // Default to empty string if not available
      date: convertDateToForm(data?.expense?.dateInvoiceKiemDich) || ''
    },
    hunTrung: {
      monney: data?.expense?.hunTrung ? String(data?.expense?.hunTrung) : '',
      vat: convertVatToForm(data?.expense?.taxHunTrung), // Default to '' if not available
      monneyVat: data?.expense?.taxMoneyHunTrung ? String(data?.expense?.taxMoneyHunTrung) : '',
      invoiceNumber: data?.expense?.invoiceHunTrung || '', // Default to empty string if not available
      date: convertDateToForm(data?.expense?.dateInvoiceHunTrung) || ''
    },
    coSoHaTang: {
      monney: data?.expense?.csht ? String(data?.expense?.csht) : '',
      invoiceNumber: data?.expense?.invoiceCSHT || '' // Default to empty string if not available
    },
    nang: {
      monney: data?.expense?.nang ? String(data?.expense?.nang) : '',
      invoiceNumber: data?.expense?.invoiceNang || '' // Default to empty string if not available
    },
    ha: {
      monney: data?.expense?.ha ? String(data?.expense?.ha) : '',
      invoiceNumber: data?.expense?.invoiceHa || '' // Default to empty string if not available
    },
    can: {
      monney: data?.expense?.can ? String(data?.expense?.can) : '',
      invoiceNumber: data?.expense?.invoiceCan || '' // Default to empty string if not available
    }
  }
}

export const convertToFormValuesShipment = (data: ShipmentType): FormValuesOrderShipment => {
  return {
    noBill: data?.noBill || '',
    pic: data?.pic || '',
    noBooking: data?.noBooking || '',
    orderTypeId: data?.orderTypeId ? String(data?.orderTypeId) : '',
    customerId: data?.customerId ? String(data?.customerId) : '',
    staffSaleId: data?.staffSaleId ? String(data?.staffSaleId) : '',
    staffKHId: data?.staffKHId ? String(data?.staffKHId) : '',
    staffTDKHId: data?.staffTDKHId ? String(data?.staffTDKHId) : '',
    staffOPId: data?.staffOPId ? String(data?.staffOPId) : '',

    freightCollect: convertFreightCollect(data),
    freightPrepaid: convertFreightPrepaid(data),
    detailTotalCostGroupOrder: convertToFormValuesShipmentTotalCostGroupOrder(data?.detailTotalCostGroupOrder)
  }
}

export const convertToFormValuesShipmentTotalCostGroupOrder = (
  data: DetailTotalCostGroupOrder
): FormValuesOrderShipment => {
  return {
    detailTotalCostGroupOrder: {},
    freightCollect: convertFreightCollect(data),
    freightPrepaid: convertFreightPrepaid(data)
  }
}
